import { FC, useCallback, useEffect } from 'react'
import { Dispatch } from 'redux'

import { ConnectedHelpTooltip } from '../../@paco/connectors'
import { Permission } from '../../@paco/entities/Permission/Permission'
import { AuthenticatedUser } from '../../@paco/entities/User/User'
import trans from '../../@paco/helpers/trans'
import { Pagination } from '../../@paco/types/paginations'

import ButtonAuthenticate from '../../components/ButtonAuthenticate/ButtonAuthenticate'
import NewFilters from '../../components/Filters/Filters'
import ItemsList from '../../components/ItemsList/ItemsList'
import OpenTracksTable from '../../components/OpenTracksTable/OpenTracksTable'
import SearchBar from '../../components/SearchBar/SearchBar'
import SidebarPage from '../../components/SidebarPage/SidebarPage'
import { TRACK_TYPE_OPEN } from '../../constants'
import UpToButExcludingDate from '../../helpers/date/UpToButExcludingDate'
import { translate } from '../../helpers/translations/translator'
import { EmploymentType, TrackViewModel } from '../../models'
import { setEmployeeSearch, setFilter } from '../../redux/filter/filterActions'
import { AppFilter } from '../../redux/filter-ts/filterModels'
import { getTracks } from '../../redux/tracks/tracksActions'

import './Tracks.scss'

interface DepartmentOption {
    isChecked: boolean;
    label: string;
    value: string;
    group: {
        label: string;
        value: string;
    }
}

interface OpenTracksProps {
    loading: boolean;
    tracksLoading: boolean;
    loadingTrackIds: string[];
    canAddTracks: boolean;
    pagination: Pagination;
    tracks: TrackViewModel[];
    dispatch: Dispatch;
    filter: AppFilter;
    currentUser: AuthenticatedUser;
    permissions: Permission[];
    departmentOptions: DepartmentOption[];
    employmentTypes: EmploymentType[];
    onAddTrackClick: () => void;
    onReviewTrackClick: (track: TrackViewModel, type: string) => void;
}

const OpenTracks: FC<OpenTracksProps> = ({
    loading,
    tracksLoading,
    loadingTrackIds,
    canAddTracks,
    pagination,
    tracks,
    dispatch,
    filter,
    currentUser,
    permissions,
    departmentOptions,
    employmentTypes,
    onAddTrackClick,
    onReviewTrackClick,
}) => {
    const getTracksData = useCallback((): void => {
        dispatch(getTracks(TRACK_TYPE_OPEN))
    }, [dispatch])

    useEffect((): void => {
        getTracksData()
    }, [getTracksData])

    useEffect((): void => {
        getTracksData()
    }, [JSON.stringify(departmentOptions.filter(option => option.isChecked))])

    const handleSetFilters = (filterObj: AppFilter): void => {
        dispatch(setFilter(filterObj, getTracksData))
    }

    const onSearchBarChange = (value: string): void => {
        dispatch(setEmployeeSearch(value))
        getTracksData()
    }

    const handleOnStopClick = (track: TrackViewModel): void => {
        const newTrack: TrackViewModel = {
            ...track,
            checkOut: new UpToButExcludingDate(new Date()).transformToUpToAndIncludingDate(),
        }

        onReviewTrackClick(newTrack, TRACK_TYPE_OPEN)
    }

    return (
        <SidebarPage
            sidebarContent={(
                <NewFilters
                    filter={filter}
                    employmentTypes={employmentTypes}
                    button={canAddTracks ? (
                        <ButtonAuthenticate
                            text={translate('pages.tracks.addTrack')}
                            onClick={onAddTrackClick}
                        />
                    ) : null}
                    filterBlocks={['contract-types']}
                    onChange={handleSetFilters}
                />
            )}
        >
            <div className="tracks-list tracks-open">
                <div className="tracks-search">
                    <SearchBar
                        placeholder={translate('common.searchEmployee')}
                        startVal={filter.employeeSearch}
                        onChange={onSearchBarChange}
                    />
                </div>

                <ItemsList
                    isLoading={loading || tracksLoading || !! loadingTrackIds.length}
                    itemsLength={tracks.length}
                    pagination={pagination}
                    emptyMessage={translate('pages.tracks.emptyList')}
                    onPagination={getTracksData}
                >
                    <OpenTracksTable
                        tracks={tracks}
                        currentUser={currentUser}
                        permissions={permissions}
                        onStopClick={handleOnStopClick}
                    />
                </ItemsList>
            </div>

            <ConnectedHelpTooltip
                index={0}
                route="open-tracks"
                subTitle={trans('help.tracks.addTrack.title')}
                text={trans('help.tracks.addTrack.text')}
                title={trans('help.tracks.title')}
                className="tracks__add-track-help-tooltip"
            />

            <ConnectedHelpTooltip
                index={1}
                route="open-tracks"
                subTitle={trans('help.tracks.filters.title')}
                text={trans('help.tracks.filters.text')}
                title={trans('help.tracks.title')}
                showMobileInfoWarning
                className="tracks__filters-help-tooltip"
            />
        </SidebarPage>
    )
}

export default OpenTracks
